import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  mode: "exact",
  exact: "",
  tolerance: "",
  min: "",
  max: "",
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};

  let mode = "exact";
  let exact = "";
  let tolerance = "";
  let min = "";
  let max = "";

  if (typeof body.exact === "number" && !Number.isNaN(body.exact)) {
    mode = "exact";
    exact = String(body.exact);
    if (typeof body.tolerance === "number" && !Number.isNaN(body.tolerance)) {
      tolerance = String(body.tolerance);
    }
  } else if (body.range) {
    mode = "range";
    if (typeof body.range.min === "number" && !Number.isNaN(body.range.min)) {
      min = String(body.range.min);
    }
    if (typeof body.range.max === "number" && !Number.isNaN(body.range.max)) {
      max = String(body.range.max);
    }
  }
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    mode,
    exact,
    tolerance,
    min,
    max,
    layout: body.layout || "top-bottom",
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function NumericModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("numeric.toasts.stem_required"));
      return;
    }

    let body;
    if (form.mode === "exact") {
      if (form.exact === "" || isNaN(Number(form.exact))) {
        toast.error(t("numeric.toasts.need_exact"));
        return;
      }
      body = {
        layout: form.layout,
        exact: Number(form.exact),
        tolerance: form.tolerance !== "" ? Number(form.tolerance) : undefined,
      };
    } else {
      if (
        form.min === "" ||
        form.max === "" ||
        isNaN(Number(form.min)) ||
        isNaN(Number(form.max))
      ) {
        toast.error(t("numeric.toasts.need_range"));
        return;
      }
      body = {
        layout: form.layout,
        range: { min: Number(form.min), max: Number(form.max) },
      };
    }

    const payload = {
      kind: "numeric",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body,
      points: Number(form.points) || 1,
      status: form.status || "published",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("numeric.toasts.updated", "سؤال عددی با موفقیت ویرایش شد")
          : t("numeric.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };
  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {" "}
      <div>
        {" "}
        <div dir={isRtl ? "rtl" : "ltr"}>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("numeric.stem_fa")}
          </label>{" "}
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("numeric.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />{" "}
        </div>{" "}
        <div dir="ltr">
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("numeric.stem_en")}
          </label>{" "}
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("numeric.stem_en_ph")}
            directionality="ltr"
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />{" "}
        </div>{" "}
      </div>
      {/* Layout Selector */}{" "}
      <div>
        {" "}
        <label className="block text-sm font-medium mb-1">
          {t("numeric.layout_label")}
        </label>{" "}
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {" "}
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                {" "}
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />{" "}
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {" "}
                  {t(`numeric.layout_question_answer_${layout}`)}{" "}
                </label>{" "}
              </div>
            )
          )}{" "}
        </div>{" "}
      </div>{" "}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("numeric.mode")}
          </label>{" "}
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.mode}
            onChange={onChange}
            name="mode"
          >
            <option value="exact">{t("numeric.mode_exact")}</option>
            <option value="range">{t("numeric.mode_range")}</option>{" "}
          </select>{" "}
        </div>{" "}
        {form.mode === "exact" ? (
          <>
            {" "}
            <div>
              {" "}
              <label className="block text-sm font-medium mb-1">
                {t("numeric.exact")}
              </label>{" "}
              <input
                className="w-full border rounded-lg px-3 py-2"
                type="number"
                name="exact"
                value={form.exact}
                onChange={onChange}
              />{" "}
            </div>{" "}
            <div>
              {" "}
              <label className="block text-sm font-medium mb-1">
                {t("numeric.tolerance")}
              </label>{" "}
              <input
                className="w-full border rounded-lg px-3 py-2"
                type="number"
                name="tolerance"
                placeholder={t("numeric.tolerance_ph")}
                value={form.tolerance}
                onChange={onChange}
              />{" "}
            </div>{" "}
          </>
        ) : (
          <>
            {" "}
            <div>
              {" "}
              <label className="block text-sm font-medium mb-1">
                {t("numeric.min")}
              </label>{" "}
              <input
                className="w-full border rounded-lg px-3 py-2"
                type="number"
                name="min"
                value={form.min}
                onChange={onChange}
              />{" "}
            </div>{" "}
            <div>
              {" "}
              <label className="block text-sm font-medium mb-1">
                {t("numeric.max")}
              </label>{" "}
              <input
                className="w-full border rounded-lg px-3 py-2"
                type="number"
                name="max"
                value={form.max}
                onChange={onChange}
              />{" "}
            </div>{" "}
          </>
        )}{" "}
      </div>{" "}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("numeric.points")}
          </label>{" "}
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            name="points"
            value={form.points}
            onChange={onChange}
          />{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("numeric.status")}
          </label>{" "}
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            name="status"
            onChange={onChange}
          >
            {" "}
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>{" "}
          </select>{" "}
        </div>{" "}
      </div>{" "}
      <div>
        {" "}
        <label className="block text-sm font-medium mb-1">
          {t("numeric.tags")}
        </label>{" "}
        <input
          className="w-full border rounded-lg px-3 py-2"
          placeholder={t("common.tags_csv")}
          name="tags"
          value={form.tags}
          onChange={onChange}
        />{" "}
      </div>{" "}
      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>{" "}
    </form>
  );
}
